import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { OrgMembership } from "./api";

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? "http://localhost:4100";

const STORAGE_USER = "sovra_logs_user";
const STORAGE_ACCESS = "sovra_logs_access_token";
const STORAGE_REFRESH = "sovra_logs_refresh_token";
const STORAGE_EXPIRES_AT = "sovra_logs_expires_at";

export interface AuthUser {
  id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  org_id: string | null;
  roles: string[];
  available_orgs: OrgMembership[];
  is_platform_admin: boolean;
  roles_in_org?: Record<string, string[]>;
}

interface SessionResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user: AuthUser;
}

interface RefreshResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  /** Refresca el `user` desde `/v1/me`. Útil cuando se crea/borra una org y
   *  hay que repoblar `available_orgs` sin pedirle al usuario que se deslogue. */
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readUser(): AuthUser | null {
  const raw = localStorage.getItem(STORAGE_USER);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

function clearStorage() {
  localStorage.removeItem(STORAGE_USER);
  localStorage.removeItem(STORAGE_ACCESS);
  localStorage.removeItem(STORAGE_REFRESH);
  localStorage.removeItem(STORAGE_EXPIRES_AT);
}

function persistSession(session: SessionResponse) {
  localStorage.setItem(STORAGE_USER, JSON.stringify(session.user));
  localStorage.setItem(STORAGE_ACCESS, session.access_token);
  localStorage.setItem(STORAGE_REFRESH, session.refresh_token);
  localStorage.setItem(
    STORAGE_EXPIRES_AT,
    String(Date.now() + session.expires_in * 1000)
  );
}

function persistRefresh(refreshed: RefreshResponse) {
  localStorage.setItem(STORAGE_ACCESS, refreshed.access_token);
  localStorage.setItem(STORAGE_REFRESH, refreshed.refresh_token);
  localStorage.setItem(
    STORAGE_EXPIRES_AT,
    String(Date.now() + refreshed.expires_in * 1000)
  );
}

let inflightRefresh: Promise<string | null> | null = null;

/**
 * Devuelve un access_token vigente. Si está a menos de 30s de expirar,
 * intenta refrescarlo. Si la refresh falla, limpia la sesión y devuelve null.
 */
export async function getAccessToken(): Promise<string | null> {
  const token = localStorage.getItem(STORAGE_ACCESS);
  const expiresAtRaw = localStorage.getItem(STORAGE_EXPIRES_AT);
  const expiresAt = expiresAtRaw ? Number(expiresAtRaw) : 0;

  if (token && Date.now() < expiresAt - 30_000) {
    return token;
  }

  const refreshToken = localStorage.getItem(STORAGE_REFRESH);
  if (!refreshToken) {
    clearStorage();
    return null;
  }

  if (inflightRefresh) return inflightRefresh;

  inflightRefresh = (async () => {
    try {
      const res = await fetch(`${API_BASE}/v1/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!res.ok) {
        clearStorage();
        return null;
      }
      const refreshed = (await res.json()) as RefreshResponse;
      persistRefresh(refreshed);
      return refreshed.access_token;
    } catch {
      clearStorage();
      return null;
    } finally {
      inflightRefresh = null;
    }
  })();

  return inflightRefresh;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(readUser);
  const [loading, setLoading] = useState(true);
  const refreshTimer = useRef<number | null>(null);

  const scheduleRefresh = useCallback(() => {
    if (refreshTimer.current) window.clearTimeout(refreshTimer.current);
    const expiresAtRaw = localStorage.getItem(STORAGE_EXPIRES_AT);
    if (!expiresAtRaw) return;
    const delta = Number(expiresAtRaw) - Date.now() - 60_000;
    if (delta <= 0) return;
    refreshTimer.current = window.setTimeout(() => {
      void getAccessToken();
    }, delta);
  }, []);

  useEffect(() => {
    void (async () => {
      const refreshToken = localStorage.getItem(STORAGE_REFRESH);
      if (refreshToken && !user) {
        // si tenemos refresh pero perdimos el user (storage corrupto), invalidamos
        clearStorage();
      }
      if (user) {
        // re-validar/refrescar al arrancar la app
        const token = await getAccessToken();
        if (!token) setUser(null);
        scheduleRefresh();
      }
      setLoading(false);
    })();

    return () => {
      if (refreshTimer.current) window.clearTimeout(refreshTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const res = await fetch(`${API_BASE}/v1/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error?.message ?? "Error iniciando sesión");
      }

      const session = (await res.json()) as SessionResponse;
      persistSession(session);
      setUser(session.user);
      scheduleRefresh();
    },
    [scheduleRefresh]
  );

  const refreshUser = useCallback(async () => {
    const token = await getAccessToken();
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/v1/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const me = (await res.json()) as Partial<AuthUser> & { user_id?: string };
      // /v1/me devuelve `user_id`, mientras AuthUser usa `id`. Normalizamos.
      const next: AuthUser = {
        id: me.user_id ?? me.id ?? "",
        email: me.email ?? "",
        display_name: me.display_name ?? null,
        avatar_url: me.avatar_url ?? null,
        org_id: me.org_id ?? null,
        roles: me.roles ?? [],
        available_orgs: me.available_orgs ?? [],
        is_platform_admin: me.is_platform_admin ?? false,
        roles_in_org: me.roles_in_org,
      };
      localStorage.setItem(STORAGE_USER, JSON.stringify(next));
      setUser(next);
    } catch {
      // Si falla, dejamos el user como está; la próxima request fallida
      // gatillará un refresh real vía getAccessToken/refresh flow.
    }
  }, []);

  const signOut = useCallback(async () => {
    const refreshToken = localStorage.getItem(STORAGE_REFRESH);
    if (refreshToken) {
      await fetch(`${API_BASE}/v1/auth/logout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      }).catch(() => {});
    }
    clearStorage();
    setUser(null);
    if (refreshTimer.current) {
      window.clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  }, []);

  const value: AuthContextValue = {
    user,
    loading,
    signIn,
    signOut,
    getAccessToken,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de <AuthProvider />");
  return ctx;
}
